/**
 * Algolia Proxy Client - Helper functions to use Algolia via proxy server
 * This file provides helper functions that mimic Algolia client methods
 * but route requests through a proxy server instead of directly to Algolia.
 * 
 * IMPORTANT: Configure your proxy server URL below
 */

const ALGOLIA_PROXY_URL = 'https://oblist-algolia-proxy.vercel.app'; // TODO: Update with your proxy server URL

/**
 * Creates a search client that mimics Algolia's client interface
 * @returns {Object} Search client with initIndex and search methods
 */
function searchClient() {
  return {
    /**
     * Initializes an index (mimics Algolia's initIndex method)
     * @param {string} indexName - Name of the Algolia index
     * @returns {Object} Index object with search method
     */
    initIndex: function(indexName) {
      return {
        /**
         * Performs a search query through the proxy server
         * Mimics Algolia's index.search() method
         * @param {string} query - Search query string
         * @param {Object} options - Search options (same as Algolia)
         * @param {boolean} [options.analytics] - Enable analytics
         * @param {number} [options.hitsPerPage] - Number of hits per page
         * @param {number} [options.page] - Page number (0-indexed)
         * @param {string} [options.userToken] - User token for personalization
         * @param {string} [options.filters] - Filter string
         * @param {boolean} [options.enablePersonalization] - Enable personalization
         * @param {Array} [options.facetFilters] - Facet filters
         * @param {Array} [options.numericFilters] - Numeric filters
         * @param {Object} [options.attributesToRetrieve] - Attributes to retrieve
         * @param {Object} [options.attributesToHighlight] - Attributes to highlight
         * @returns {Promise<{hits: Array, queryID: string, nbHits: number, nbPages: number, page: number, processingTimeMS: number}>}
         */
        search: async function(query, options = {}) {
          try {
            const requestBody = {
              requests: [{
                indexName: indexName,
                query: query || '',
                params: options
              }]
            };

            const response = await fetch(`${ALGOLIA_PROXY_URL}/search`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
              throw new Error(`Algolia proxy error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Handle response - if it's a results array (multi-index format), extract first result
            // Otherwise, use the data directly (single result format)
            const result = data.results && data.results.length > 0 
              ? data.results[0] 
              : data;
            
            // Ensure the response matches Algolia's search response structure
            return {
              hits: result.hits || [],
              queryID: result.queryID || null,
              nbHits: result.nbHits || 0,
              nbPages: result.nbPages || 0,
              page: result.page || 0,
              processingTimeMS: result.processingTimeMS || 0,
              ...result // Include any additional properties from the proxy response
            };
          } catch (error) {
            console.error('[Algolia Proxy] Index search error:', error);
            throw error;
          }
        }
      };
    },

    /**
     * Direct search method for multi-index searches
     * Mimics Algolia's client.search() method that accepts an array of search requests
     * @param {Array} requests - Array of search request objects
     * @param {string} requests[].indexName - Name of the Algolia index
     * @param {string} requests[].query - Search query string
     * @param {Object} requests[].params - Search parameters (analytics, hitsPerPage, etc.)
     * @returns {Promise<{results: Array}>} Response with results array matching request order
     */
    search: async function(requests) {
      try {
        // Handle single request (backward compatibility)
        if (!Array.isArray(requests)) {
          throw new Error('client.search() expects an array of search requests');
        }

        const requestBody = {
          requests: requests.map(req => ({
            indexName: req.indexName,
            query: req.query || '',
            params: req.params || {}
          }))
        };

        const response = await fetch(`${ALGOLIA_PROXY_URL}/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`Algolia proxy error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Ensure the response matches Algolia's multi-index search response structure
        return {
          results: data.results || requests.map(() => ({
            hits: [],
            queryID: null,
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0
          })),
          ...data // Include any additional properties from the proxy response
        };
      } catch (error) {
        console.error('[Algolia Proxy] Multi-index search error:', error);
        throw error;
      }
    },

    /**
     * Adds Algolia agent (for compatibility, no-op in proxy)
     * @param {string} agent - Agent string
     */
    addAlgoliaAgent: function(agent) {
      // No-op for proxy client, but kept for API compatibility
      console.log('[Algolia Proxy] Agent added:', agent);
    }
  };
}

/**
 * Creates a recommend client that mimics Algolia's recommend client interface
 * @param {string} appId - Algolia Application ID (for reference, not sent to proxy)
 * @param {string} apiKey - Algolia API Key (for reference, not sent to proxy)
 * @returns {Object} Recommend client with getRecommendations method
 */
function recommendClient(appId, apiKey) {
  return {
    /**
     * Gets product recommendations through the proxy server
     * Mimics Algolia's recommendClient.getRecommendations() method
     * @param {Object} options - Recommendation options
     * @param {Array} options.requests - Array of recommendation request objects
     * @param {string} options.requests[].indexName - Name of the Algolia index
     * @param {string} options.requests[].objectID - Object ID of the product to get recommendations for
     * @param {string} options.requests[].model - Recommendation model ('related-products', 'looking-similar', 'trending-items', etc.)
     * @param {number} [options.requests[].threshold] - Minimum score threshold
     * @param {number} [options.requests[].maxRecommendations] - Maximum number of recommendations
     * @returns {Promise<{results: Array}>} Response with results array matching request order
     */
    getRecommendations: async function(options) {
      try {
        if (!options || !options.requests || !Array.isArray(options.requests)) {
          throw new Error('getRecommendations() expects an object with a requests array');
        }

        const requestBody = {
          requests: options.requests.map(req => ({
            indexName: req.indexName,
            objectID: req.objectID,
            model: req.model,
            threshold: req?.threshold || 10,
            maxRecommendations: req?.maxRecommendations || 10
          }))
        };

        const response = await fetch(`${ALGOLIA_PROXY_URL}/recommend`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`Algolia proxy error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Ensure the response matches Algolia's recommendations response structure
        return {
          results: data.results || options.requests.map(() => ({
            hits: [],
            queryID: null
          })),
          ...data // Include any additional properties from the proxy response
        };
      } catch (error) {
        console.error('[Algolia Proxy] Get recommendations error:', error);
        throw error;
      }
    }
  };
}

// Export for use in the theme
window.AlgoliaProxy = {
  searchClient: searchClient,
  recommendClient: recommendClient,
  // Client search method (multi-index) - convenience method
  search: async function(requests) {
    const client = searchClient();
    return await client.search(requests);
  }
};

// Also export as algoliasearch for drop-in replacement (optional)
// Uncomment if you want to replace algoliasearch() calls directly
// window.algoliasearch = searchClient;
